import { useCallback, useMemo } from 'react';
import type { StoredEmailTemplates } from '#types/System.Parameters.types';
import { substituteEmailTemplate, EmailTemplateContext } from './substituteEmailTemplate';

/**
 * @file useEmailTemplateResolution.ts
 * @description Shared resolution for email-template defaults used by
 * FormEmailModal and WorkflowParameterSidebar.
 *
 * Returns the default-template entry, pre-substituted subject/message/signature,
 * Select options, and a `resolveFor(id)` helper to switch templates at compose time.
 */

export type ResolvedEmailTemplateFields = {
    subject: string;
    message: string;
    signature: string;
};

export const useEmailTemplateResolution = (
    templates: StoredEmailTemplates | undefined,
    context: EmailTemplateContext,
) => {
    const entries = useMemo(() => templates?.entries ?? [], [templates?.entries]);
    const defaultEntryId = templates?.defaultEntryId;

    const initialTemplate = useMemo(
        () => entries.find((t) => t.id === defaultEntryId) ?? entries[0],
        [entries, defaultEntryId],
    );

    const initialSubject = useMemo(
        () => substituteEmailTemplate(initialTemplate?.subject ?? '', context),
        [initialTemplate?.subject, context],
    );
    const initialMessage = useMemo(
        () => substituteEmailTemplate(initialTemplate?.message ?? '', context),
        [initialTemplate?.message, context],
    );
    const initialSignature = useMemo(
        () => substituteEmailTemplate(initialTemplate?.signature ?? '', context),
        [initialTemplate?.signature, context],
    );

    const selectOptions = useMemo(
        () => entries.map((t) => ({ value: t.id, label: t.label || '(unnamed)' })),
        [entries],
    );

    const resolveFor = useCallback(
        (templateId: string): ResolvedEmailTemplateFields | null => {
            const t = entries.find((e) => e.id === templateId);
            if (!t) return null;
            return {
                subject: substituteEmailTemplate(t.subject, context),
                message: substituteEmailTemplate(t.message, context),
                signature: substituteEmailTemplate(t.signature, context),
            };
        },
        [entries, context],
    );

    return {
        entries,
        initialTemplate,
        initialSubject,
        initialMessage,
        initialSignature,
        selectOptions,
        resolveFor,
    };
};
